import { Ref } from 'vue';
import i18n from '../../i18n';
import { UserAvatar } from '../users/models';
import { loadLocaleMessages } from '../../i18n';
import { UserPreference } from '../core/models';
import { IUIState, UserInformation } from './models';
import { ResolveParams } from '../../core/models/routing';
import { httpClient } from '../../core/axios/http-client';
//import { useNotificationsStore } from '../notifications/store';
import { getUserPreferenceValue } from '../../core/utils/user-preferences-utils';
import { LanguageCodes, USER_PREFERENCE_KEY_LANGUAGE, USER_PREFERENCE_KEY_CHAT_FULL_WIDTH } from '../../shared/constants';

const useUiStoreActions = (state: Ref<IUIState>) => {
  const loadUserPreferences = () => {
    return httpClient.get<void, UserPreference[]>('users/user-preferences').then((response) => {
      state.value.userPreferences = response;
      const fullWidthPref = response.find((p) => p.preferenceKey === USER_PREFERENCE_KEY_CHAT_FULL_WIDTH);
      if (fullWidthPref) state.value.isChatFullWidth = fullWidthPref.preferenceValue === 'true';
    });
  };

  const configureLocaleBasedOnUserPreference = async () => {
    let locale = getUserPreferenceValue(USER_PREFERENCE_KEY_LANGUAGE, LanguageCodes.en);
    await loadLocaleMessages(i18n, locale);
  };

  const fetchMainData = (_params: ResolveParams) => {
    return new Promise<void>((resolve, reject) => {
      loadUserPreferences()
        .then(() => {
          const loadUserInformationPromise = loadUserInfo();

          //useNotificationsStore().getOngoingBackgroundServicesNotifications()
          Promise.all([loadUserInformationPromise])
            .then(async () => {
              await configureLocaleBasedOnUserPreference();
              resolve();
            })
            .catch((reason) => reject(reason));
        })
        .catch((reason) => reject(reason));
    });
  };

  const saveUserPreference = (payload: UserPreference) => {
    return httpClient.post('users/save-user-preference', payload).then(() => {
      const index = state.value.userPreferences.findIndex((x) => x.preferenceKey === payload.preferenceKey);
      if (index !== -1) {
        state.value.userPreferences[index].preferenceValue = payload.preferenceValue;
      } else {
        state.value.userPreferences.push(payload);
      }
    });
  };

  const loadUserInfo = () => {
    return httpClient.get<void, UserInformation>(`users/user-info`).then((response) => {
      state.value.sessionUserInfo = response;
      state.value.userAvatarsCache = response.avatar ? [response.avatar] : [];
    });
  };

  const getUserAvatarFromCache = (idUser: string) => {
    return state.value.userAvatarsCache.find((x) => x.idUser === idUser);
  };

  const cacheUserAvatars = (userIds: string[]) => {
    const distinctUserids = Array.from(new Set(userIds));
    const cachedAvatars = state.value.userAvatarsCache.filter((x) => distinctUserids.includes(x.idUser));
    const notCachedAvatars = distinctUserids.filter((x) => !cachedAvatars.find((y) => y.idUser == x));

    if (notCachedAvatars.length)
      return httpClient.post<void, UserAvatar[]>(`users/user-avatars`, notCachedAvatars).then((response) => {
        state.value.userAvatarsCache = [...state.value.userAvatarsCache, ...response];
      });
  };

  const toggleChatFullWidth = () => {
    state.value.isChatFullWidth = !state.value.isChatFullWidth;
    saveUserPreference({
      preferenceKey: USER_PREFERENCE_KEY_CHAT_FULL_WIDTH,
      preferenceValue: state.value.isChatFullWidth.toString(),
    });
  };

  const signOut = () => {
    httpClient.get<void, any>('Account/sign-out');
  };

  return {
    signOut,
    toggleChatFullWidth,
    fetchMainData,
    cacheUserAvatars,
    saveUserPreference,
    getUserAvatarFromCache,
    configureLocaleBasedOnUserPreference,
    loadUserInfo,
  };
};

export default useUiStoreActions;
